import React, { useRef, useState } from "react";

import {
	Button,
	ConfirmModal,
	InfoMessage,
	Modal,
	Table,
	TTableColumn,
} from "@amphore-dev/amphore-lib";
import { FormikProps } from "formik";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";

import { useAuth } from "@/hooks/useAuth";
import { usePlanGate } from "@/hooks/usePlanGate";

import {
	CompanyForm,
	PlanUsage,
	WorkspacePicker,
} from "@/components/organisms";
import { PageLayout } from "@/components/templates";

import {
	groupByWorkspace,
	MANAGING_ROLES,
	workspaceLabel,
} from "@/utils/UWorkspaces";

import { companyKeys } from "@/constants/CQueryKeys";

import { TCompany, TCompanyFormValues } from "@/types";
import { TCompanyKind } from "@/types";

import { promiseToast } from "@/modules/index";

const emptyCompany: TCompanyFormValues = {
	name: "",
	siren: "",
	vatNumber: "",
	email: "",
	address: "",
	postalCode: "",
	city: "",
	logo: null,
};

interface ICompanyListViewProps {
	kind: TCompanyKind;
}

/**
 * The user's companies (the ones stamped on reports) or their clients
 * (the ones reports are sent to) - one view, see `kinds.ts`. A row opens
 * the detail page, "New" creates one from the same form as the
 * onboarding step.
 */
export const CompanyListView: React.FC<ICompanyListViewProps> = ({ kind }) => {
	const navigate = useNavigate();
	const gate = usePlanGate(kind.collection);
	const { user } = useAuth();
	// Clients come from every workspace that reaches the user; a new one
	// goes to one they run (their own by default).
	const isClients = kind.collection === "clients";
	const managing = (user?.workspaces ?? []).filter((w) =>
		MANAGING_ROLES.includes(w.role)
	);
	const [createIn, setCreateIn] = useState<number | undefined>(undefined);
	const queryClient = useQueryClient();
	const [isCreating, setIsCreating] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<TCompany | null>(null);
	const createFormRef = useRef<FormikProps<TCompanyFormValues>>(null);

	const { isLoading, error, data } = useQuery({
		queryKey: companyKeys.collection(kind.collection),
		queryFn: () => CompaniesService.fetchAll(kind.collection),
	});
	const items = data ?? [];

	// Same sequence as the onboarding submit: the logo has its own upload
	// endpoint, so it needs the company's id first.
	const createMutation = useMutation({
		mutationFn: async ({ logo, ...fields }: TCompanyFormValues) => {
			const company = await CompaniesService.create(
				{
					...fields,
					name: fields.name.trim(),
					...(isClients &&
						createIn !== undefined && { workspaceId: createIn }),
				},
				kind.collection
			);
			if (logo) {
				await CompaniesService.updateLogo(logo, company.id);
			}
			return company;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: companyKeys.collection(kind.collection),
			});
			setIsCreating(false);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => CompaniesService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: companyKeys.collection(kind.collection),
			});
		},
	});

	const handleCreate = (values: TCompanyFormValues) => {
		promiseToast(createMutation.mutateAsync(values));
	};

	// A client shared with the user, or reached through a workspace role,
	// is listed but its page is not theirs to open (canOpen).
	const openCompany = (item: TCompany) => {
		if (item.canOpen === false) return;
		navigate(`${kind.basePath}/${item.id}`);
	};
	const groups = isClients
		? groupByWorkspace(items)
		: [{ workspace: null, items }];

	// Built inside the component: `t()` is i18next's plain export, a
	// module-level constant would resolve the labels before init.
	const columns: TTableColumn<TCompany>[] = [
		{
			key: "contextMenu",
		},
		{
			key: "logo",
			label: t("company.logo"),
			width: "8rem",
			render: (item) =>
				item.logo ? (
					<img
						src={item.logo}
						alt={item.name}
						className="h-12 min-w-12 object-contain"
					/>
				) : null,
			truncate: false,
		},
		{
			key: "name",
			label: t("company.name"),
			sortable: true,
		},
		{
			key: "city",
			label: t("company.city"),
			render: (item) => item.city || "-",
		},
		{
			key: "email",
			label: t("company.email"),
			render: (item) => item.email || "-",
		},
	];

	// Locked at the plan's cap: the click opens the upgrade modal instead.
	// A courtesy - the API refuses regardless.
	const createButton = gate.button({
		label: t(`${kind.i18n}.new`),
		color: "primary" as const,
		onClick: () => setIsCreating(true),
	});

	return (
		<>
			<PageLayout
				title={t(kind.title)}
				isLoading={isLoading}
				error={!!error}
				actions={[<PlanUsage key="usage" on={kind.collection} />]}
				buttons={[createButton]}
			>
				{items.length === 0 ? (
					<div className="flex flex-col items-start gap-4">
						<InfoMessage>{t(`${kind.i18n}.empty`)}</InfoMessage>
						<Button {...createButton}>{createButton.label}</Button>
					</div>
				) : (
					<div className="flex flex-col gap-8">
						{groups.map((group) => {
							const mine =
								!group.workspace || group.workspace.mine;
							const key = mine ? "own" : group.workspace!.id;
							const runs = group.items.every(
								(c) => c.canOpen !== false
							);
							return (
								<section
									key={key}
									data-testid={`workspace-group-${key}`}
									className="flex flex-col gap-3"
								>
									{groups.length > 1 && (
										<h2 className="text-lg font-semibold">
											{workspaceLabel(
												group.workspace ?? {
													name: null,
													ownerName: "",
													mine: true,
												}
											)}
										</h2>
									)}
									<Table
										isLoading={isLoading}
										items={group.items}
										getItemKey={(item) => item.id}
										onRowClick={openCompany}
										columns={columns}
										rowActions={
											runs
												? [
														{
															label: t(
																"actions.edit"
															),
															onClick:
																openCompany,
															picto: "pen",
														},
														{
															label: t(
																"actions.delete"
															),
															onClick:
																setDeleteTarget,
															picto: "trash",
														},
													]
												: []
										}
									/>
								</section>
							);
						})}
					</div>
				)}
			</PageLayout>
			<Modal
				open={isCreating}
				onClose={() => setIsCreating(false)}
				title={t(`${kind.i18n}.create.title`)}
				size="lg"
				footer={
					<div className="flex justify-end gap-2">
						<Button
							variant="ghost"
							onClick={() => setIsCreating(false)}
						>
							{t("actions.cancel")}
						</Button>
						<Button
							color="primary"
							isLoading={createMutation.isPending}
							onClick={() => createFormRef.current?.submitForm()}
						>
							{t(`${kind.i18n}.create.submit`)}
						</Button>
					</div>
				}
			>
				{/* Mounted with the modal so each opening starts blank. */}
				{isCreating && isClients && (
					<WorkspacePicker
						options={managing}
						value={
							createIn ??
							managing.find((w) => w.role === "OWNER")?.id
						}
						onChange={setCreateIn}
						className="mb-4 max-w-none"
					/>
				)}
				{isCreating && (
					<CompanyForm
						innerRef={createFormRef}
						initialValues={emptyCompany}
						onSubmit={handleCreate}
						logoInputId={`${kind.collection}-create-logo`}
					/>
				)}
			</Modal>
			<ConfirmModal
				open={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				size="sm"
				title={t(`${kind.i18n}.delete.title`)}
				onConfirm={() => {
					promiseToast(deleteMutation.mutateAsync(deleteTarget!.id));
					setDeleteTarget(null);
				}}
				confirmText={t("actions.delete")}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
			>
				{t(`${kind.i18n}.delete.message`)}
			</ConfirmModal>
		</>
	);
};
